/**
 * Rhyme Category Display Metadata
 * Human-readable names, explanations, and tier information for each RhymeCategory
 *
 * Used by RhymePopover to show category details and educational content
 */

import { RhymeCategory } from './types'

/**
 * Display metadata for each rhyme category
 *
 * Provides:
 * - Human-readable name for UI display
 * - Educational explanation of the rhyme type
 * - Strength tier classification (Strong/Mid/Weak)
 */
export interface CategoryDisplayInfo {
  /** Human-readable category name */
  name: string
  /** Educational explanation of the rhyme type */
  explanation: string
  /** Strength tier classification */
  tier: 'Strong' | 'Mid' | 'Weak' | 'None'
}

/**
 * Category display metadata for all rhyme categories
 *
 * Maps each RhymeCategory enum to its display information
 * Used by RhymePopover to show progressive disclosure of rhyme details
 */
export const CATEGORY_DISPLAY: Record<RhymeCategory, CategoryDisplayInfo> = {
  [RhymeCategory.PERFECT]: {
    name: 'Perfect Rhyme',
    explanation: 'Identical sounds from the stressed vowel onward',
    tier: 'Strong',
  },
  [RhymeCategory.FAMILY_VOICED]: {
    name: 'Voiced Family',
    explanation: 'Final consonants differ only by voicing (voiced)',
    tier: 'Strong',
  },
  [RhymeCategory.FAMILY_UNVOICED]: {
    name: 'Unvoiced Family',
    explanation: 'Final consonants differ only by voicing (unvoiced)',
    tier: 'Mid',
  },
  [RhymeCategory.ADDITIVE]: {
    name: 'Additive Rhyme',
    explanation: 'One word adds a consonant to the other',
    tier: 'Mid',
  },
  [RhymeCategory.SUBTRACTIVE]: {
    name: 'Subtractive Rhyme',
    explanation: 'One word removes a consonant from the other',
    tier: 'Mid',
  },
  [RhymeCategory.ASSONANCE]: {
    name: 'Assonance',
    explanation: 'Matching stressed vowels with different consonants',
    tier: 'Weak',
  },
  [RhymeCategory.CONSONANCE]: {
    name: 'Consonance',
    explanation: 'Matching final consonants with different vowels',
    tier: 'Weak',
  },
  [RhymeCategory.NONE]: {
    name: 'No Rhyme',
    explanation: 'No rhyme pattern detected',
    tier: 'None',
  },
}
